/**
 * Camera-based QR scanner for sites where the kiosk's HID scanner can't
 * read QR codes off a phone screen (e.g. laser-only barcode scanners).
 *
 * - Continuously decodes frames via jsqr; on first valid decode emits one
 *   `onScan` and stops.
 * - Client-side dedupe by (qrPayload, timeStep) prevents the same window
 *   firing multiple resolves if the parent forgets to close the scanner.
 * - Auto-closes after 30s of no detection (`onTimeout`).
 * - Mirrors success/error UX of the HID scan path — the parent runs the
 *   same `resolvePlayer({ qrPayload })` call regardless of input source.
 */

import { useEffect, useRef, useState } from "react";
import {
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import jsQR from "jsqr";
import { parseKioskQrPayload } from "@medicoach/api-utils/kiosk";

const SCAN_TIMEOUT_MS = 30_000;
const FRAME_INTERVAL_MS = 100;

interface CameraQrScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (qrPayload: string) => void;
  onTimeout?: () => void;
}

// Downscale the input frame before handing to jsQR — full 1080p frames
// pin the CPU on lower-end Samsung tablets; 640px wide is plenty for QR
// detection from arm's length.
const TARGET_DECODE_WIDTH = 640;

export const CameraQrScanner = ({
  open,
  onClose,
  onScan,
  onTimeout,
}: CameraQrScannerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSubmittedRef = useRef<string | null>(null);
  const [status, setStatus] = useState<"idle" | "ready" | "denied">("idle");

  // Stash callbacks in refs so the camera-stream effect can depend on
  // only `[open]`. Inline closures from the parent would otherwise force
  // a stream re-acquire (and a 30s timer reset) on every parent render —
  // viewfinder flicker, dropped frames, and the auto-close never fires.
  const onCloseRef = useRef(onClose);
  const onScanRef = useRef(onScan);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus("idle");
    lastSubmittedRef.current = null;

    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("denied");
      }
    })();

    const timeoutId = setTimeout(() => {
      onTimeoutRef.current?.();
      onCloseRef.current();
    }, SCAN_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (frameTimerRef.current) clearInterval(frameTimerRef.current);
      frameTimerRef.current = null;
      const s = streamRef.current;
      streamRef.current = null;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, [open]);

  // Frame loop: only runs while the camera is `ready`. On a successful
  // decode that hasn't been submitted in this window, fire one onScan and
  // tear down — never spam the resolve endpoint with consecutive frames
  // of the same QR. The dedupe is best-effort client-side; the server's
  // KioskQrConsumed record is the actual replay defence.
  useEffect(() => {
    if (!open || status !== "ready") return;

    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;
      if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

      const sw = video.videoWidth;
      const sh = video.videoHeight;
      if (!sw || !sh) return;
      const scale = Math.min(1, TARGET_DECODE_WIDTH / sw);
      const dw = Math.round(sw * scale);
      const dh = Math.round(sh * scale);
      canvas.width = dw;
      canvas.height = dh;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, dw, dh);
      const image = ctx.getImageData(0, 0, dw, dh);
      const code = jsQR(image.data, image.width, image.height, {
        inversionAttempts: "dontInvert",
      });
      if (!code) return;

      const data = code.data.trim();
      const parsed = parseKioskQrPayload(data);
      if (!parsed) return;

      // Dedupe key includes timeStep so consecutive frames of the same
      // 30s-window QR fire exactly one resolve. A fresh QR after a rotate
      // gets through.
      const key = `${parsed.playerID}.${parsed.timeStep}`;
      if (lastSubmittedRef.current === key) return;
      lastSubmittedRef.current = key;

      if (frameTimerRef.current) {
        clearInterval(frameTimerRef.current);
        frameTimerRef.current = null;
      }
      onScanRef.current(data);
    };

    frameTimerRef.current = setInterval(tick, FRAME_INTERVAL_MS);
    return () => {
      if (frameTimerRef.current) clearInterval(frameTimerRef.current);
      frameTimerRef.current = null;
    };
  }, [open, status]);

  return (
    <Modal opened={open} onClose={onClose} title="Scan player's QR Code" size="lg">
      <Stack spacing="md">
        {status === "idle" && (
          <Group>
            <Loader size="sm" />
            <Text size="sm">Opening camera…</Text>
          </Group>
        )}
        {status === "denied" && (
          <Stack spacing={4}>
            <Text size="sm" color="red">
              Couldn't access the camera.
            </Text>
            <Text size="xs" color="dimmed">
              Open the kiosk&apos;s browser settings → Privacy → Camera, allow
              this site, then close and reopen this dialog.
            </Text>
          </Stack>
        )}
        <video
          ref={videoRef}
          playsInline
          muted
          style={{
            width: "100%",
            maxHeight: 480,
            borderRadius: 8,
            background: "#000",
            display: status === "ready" ? "block" : "none",
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <Text size="xs" color="dimmed">
          Hold the player's phone in front of the camera. The scanner closes
          automatically after a successful scan or 30 seconds.
        </Text>
        <Group position="right">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
